.panel {
  grid-column: var(--panel-column, span 6);
  grid-row: var(--panel-row, auto);
  display: flex;
  flex-direction: column;
  /* A panel reads from its upper edge; the remaining lower area is deliberate breathing room. */
  justify-content: flex-start;
  gap: 0.15em;
  min-width: 0;
  min-height: 0;
  padding: clamp(0.5rem, 0.8vw, var(--gap));
  border: 1px solid var(--edge);
  border-radius: var(--radius);
  background: var(--surface-raised);
  position: relative;
  isolation: isolate;
  overflow: hidden;
  line-height: 1.2;
  container-name: panel;
  container-type: size;
}
.attentionActive {
  outline: 0.14rem solid var(--status-critical);
  outline-offset: 0.16rem;
}
.content {
  position: relative;
  z-index: 1;
  display: flex;
  flex: 0 1 auto;
  flex-direction: column;
  justify-content: flex-start;
  gap: 0.1em;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}
.evidence {
  display: contents;
}
/* The source link owns its corner, so the label and evidence never render beneath it. */
.panel:has(.sourceAction) .content {
  padding-inline-end: 2.25em;
}
.content > *,
.details > * {
  min-width: 0;
  max-width: 100%;
}
.content:has([data-running-progress="runway"]),
.content:has([data-running-progress="signal-field"]) {
  flex: 1 1 0;
}
.statusBand {
  display: grid;
  flex: 1 1 0;
  grid-template-rows: 2.6em minmax(0, 1fr) auto;
  gap: 0;
}
.statusBand > [data-panel-anchor="status"] {
  position: absolute;
  inset-inline: 0;
  top: 50%;
  grid-row: 2;
  align-self: center;
  transform: translateY(-50%);
}
.statusBand > [data-panel-anchor="evidence"] {
  display: block;
  grid-row: 3;
  align-self: end;
}
.content:not(.statusBand) > [data-panel-anchor="status"] {
  display: contents;
}
.statusBand > [data-panel-anchor="status"],
.statusBand > [data-panel-anchor="evidence"] {
  min-width: 0;
}
.error {
  border-color: var(--status-serious);
}
.wide {
  grid-column: span 12;
}
.density-comfortable {
  padding: 0.65em;
  gap: 0.1em;
}
.density-comfortable .content {
  gap: 0.1em;
}
.density-compact {
  min-height: 3.5em;
  padding: 0.2em;
  gap: 0;
  line-height: 1.05;
  justify-content: flex-start;
}
.density-compact .content {
  gap: 0;
}
.label {
  margin: 0;
  overflow-wrap: anywhere;
  font-size: 1em;
  font-weight: 600;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: var(--ink-secondary);
}
.statusBand .label {
  grid-row: 1;
}
.typeGlyph {
  display: none;
}
.identityText {
  overflow-wrap: anywhere;
}
.density-compact .label {
  font-size: 0.8em;
}
.hint {
  margin: 0;
  font-size: 0.75em;
  color: var(--ink-muted);
  overflow-wrap: anywhere;
}
.branch {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  overflow-wrap: anywhere;
}
.observed {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35em;
  align-items: center;
}
.meta {
  display: var(--panel-meta-display, block);
}
.metaValue {
  min-width: 0;
  overflow-wrap: anywhere;
}
.compactValue {
  display: none;
}
.warning {
  color: var(--status-warning);
}
.serious {
  color: var(--status-serious);
}
.status {
  margin: 0;
  font-size: 1.35em;
  font-weight: 650;
}
.statusGlyph {
  display: inline-block;
}
.compactOnlyStatus {
  display: none;
}
.density-compact .status {
  font-size: 1.1em;
}
.passed {
  color: var(--status-good);
}
.failed {
  color: var(--status-critical);
}
.warning,
.running {
  color: var(--status-warning);
}
.cancelled,
.unknown {
  color: var(--ink-secondary);
}
.sourceAction {
  position: absolute;
  z-index: 2;
  top: 0.35em;
  right: 0.35em;
  display: inline-grid;
  width: 2em;
  height: 2em;
  place-items: center;
  border-radius: 0.3em;
  color: var(--ink-secondary);
  font-size: 0.85em;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
}
.sourceAction:hover {
  color: var(--ink-primary);
  background: color-mix(in srgb, var(--ink-primary) 8%, transparent);
}
.sourceAction:focus-visible {
  outline: 2px solid var(--ink-primary);
  outline-offset: 2px;
}

.density-comfortable :is(.hint, .observed),
.density-compact :is(.hint, .observed) {
  font-size: 0.65em;
}
@container panel (min-width: 48rem) {
  .density-comfortable.shallow,
  .density-auto.shallow {
    padding: 0.45em 0.65em;
  }
  .density-comfortable.shallow .content,
  .density-auto.shallow .content {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: baseline;
    column-gap: 0.7em;
    row-gap: 0.15em;
  }
  .density-comfortable.shallow .status,
  .density-auto.shallow .status {
    flex: 0 0 auto;
    font-size: 1em;
  }
  .density-comfortable.shallow :is(.label, .observed),
  .density-auto.shallow :is(.label, .observed) {
    flex: 0 0 auto;
  }
}
@container panel (min-width: 12rem) {
  .density-compact.shallow .content,
  .density-auto.shallow .content {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: baseline;
    column-gap: 0.7em;
    row-gap: 0.15em;
  }
  .density-compact.shallow :is(.label, .observed),
  .density-auto.shallow :is(.label, .observed) {
    flex: 0 0 auto;
  }
}
/* Width and height use the same scale steps, so a narrow or tall cell gets the same text budget. */
@container panel (max-width: 22rem) {
  .content,
  .sourceAction {
    font-size: 0.8em;
  }
}
@container panel (max-width: 15rem) {
  .content,
  .sourceAction {
    font-size: 0.9em;
  }
}
@container panel (max-height: 10rem) {
  .content,
  .sourceAction {
    font-size: 0.8em;
  }
}
@container panel (max-height: 7rem) {
  .content,
  .sourceAction {
    font-size: 0.9em;
  }
}
/* Responsive layout may flow authored cells into one column; content never escapes its cell. */
@media (max-width: 64rem) {
  .panel {
    grid-column: 1 / -1;
    grid-row: auto;
    min-height: 6em;
    --stacked-card-reading-line-height: 1.25;
    /* Desktop cells need size containment to honor their assigned grid height. Stacked cards
       instead need their readable evidence to contribute to height; retain inline queries for
       width-responsive treatments without containing the block axis. */
    container-type: inline-size;
  }
  /* Stacked cards grow for their evidence; the panel still clips only its decorative field. */
  .content {
    flex-shrink: 0;
    min-height: auto;
    overflow: visible;
    line-height: var(--stacked-card-reading-line-height);
  }
  .density-comfortable .content,
  .density-compact .content,
  .density-auto .content {
    flex-direction: column;
    align-items: stretch;
  }
}

@container panel (max-width: 11rem) and (max-height: 9rem) {
  .content {
    --panel-meta-display: flex;
  }
  .label {
    font-size: 0.85em;
  }
  .status {
    font-size: 1.05em;
  }
}
@container panel (max-height: 5rem) {
  .content {
    --panel-meta-display: flex;
  }
  .label {
    font-size: 0.85em;
  }
  .status {
    font-size: 1.05em;
  }
}

@container panel (max-width: 13rem) {
  .density-compact .content {
    --panel-meta-display: flex;
  }
  .density-compact .content > .meta {
    font-size: 0.6em;
  }
}
@container panel (max-width: 13rem) {
  /* Compact evidence keeps its full wording for assistive technology and the hover title. */
  .meta:has(.compactValue) .fullValue {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  .meta:has(.compactValue) .compactValue {
    display: inline;
  }
}
@container panel (max-width: 8rem) {
  /* Text-light is an internal sub-mode: secondary text remains in the accessibility tree. */
  .content {
    --panel-meta-display: flex;
    gap: 0.05em;
  }
}
@container panel (min-height: 24rem) and (max-width: 13rem) {
  .content {
    --panel-meta-display: flex;
    gap: 0.05em;
  }

  .statusBand {
    grid-template-rows: 2.6em minmax(0, 1fr) 5em;
  }
}
@container panel (max-height: 4rem) {
  .content {
    --panel-meta-display: flex;
    gap: 0.05em;
  }
}

@container panel (max-width: 13rem) and (max-height: 10rem) {
  .density-comfortable .content > .meta {
    font-size: 0.68em;
  }
}

@container panel (max-width: 18rem) and (max-height: 10rem) {
  .density-compact .content > .meta {
    font-size: 0.6em;
  }
}

.meta {
  align-items: center;
  gap: 0.2em;
  min-width: 0;
}
.metaValue {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Portrait cells retain their authored footprint but become a symbol-led scan surface. */
@container panel (max-width: 13rem) and (min-height: 10rem) and (max-aspect-ratio: 3 / 4) {
  .content {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, max-content));
    grid-template-rows: min-content;
    align-content: start;
    justify-content: center;
    gap: 0.45em;
  }
  .label {
    grid-column: 1;
    grid-row: 1;
    margin: 0;
  }
  .typeGlyph {
    display: inline-block;
    color: var(--ink-secondary);
    font-size: 1.8em;
    line-height: 1;
  }
  .status {
    grid-column: 2;
    grid-row: 1;
    margin: 0;
    font-size: 1.8em;
    line-height: 1;
  }
  .compactOnlyStatus {
    display: block;
  }
  /* Text-light portrait treatments retain their full wording for assistive technology. */
  .identityText,
  .statusText,
  .content > [data-panel-evidence-priority="secondary"] > :not([data-panel-status]),
  .content > [data-panel-anchor="evidence"][data-panel-evidence-priority="secondary"] {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  .content > [data-panel-anchor="status"] {
    grid-column: 2;
    grid-row: 1;
    align-self: start;
  }
  /* Health tiles keep their upright identity while preserving the shared scan bands. */
  .statusBand {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: 2.8em minmax(0, 1fr) 5em;
    align-content: stretch;
    justify-content: stretch;
    gap: 0;
  }
  .statusBand .label {
    display: flex;
    flex-direction: column;
    grid-column: 1;
    grid-row: 1;
    width: 100%;
    align-items: start;
    justify-content: center;
    gap: 0.1em;
    font-size: 0.78em;
    line-height: 1.15;
    text-align: center;
  }
  .statusBand .identityText {
    /* The title gets its own line, so the large type glyph cannot reduce it to one character. */
    flex: none;
    width: 100%;
    min-width: 0;
    position: static;
    height: auto;
    padding: 0;
    margin: 0;
    overflow: visible;
    clip: auto;
    white-space: normal;
  }
  .statusBand > [data-panel-anchor="status"] {
    position: static;
    inset-inline: auto;
    top: auto;
    grid-column: 1;
    grid-row: 2;
    align-self: center;
    justify-self: center;
    transform: none;
  }
  .statusBand > [data-panel-anchor="evidence"] {
    position: static;
    display: block;
    grid-column: 1;
    grid-row: 3;
    width: auto;
    height: auto;
    padding: 0;
    margin: 0;
    overflow: hidden;
    clip: auto;
    white-space: normal;
  }
  .statusBand .status {
    font-size: 2.1em;
  }
}
